import React, { useState } from 'react';
import {
    Paper,
    Box,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Tooltip,
    Typography,
    TextField,
    Button,
    Collapse,
    Divider,
    InputAdornment,
} from '@mui/material';
import {
    ChevronLeft,
    ChevronRight,
    Refresh,
    Tune,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { MONTH_KEYS, getMonthName } from '../../utils/monthlyDataUtils';
import { DEFAULT_FT_RATE } from '../../utils/powerCostUtils';

function PowerCostControlBar({
    selectedYear,
    onYearChange,
    selectedMonth,
    onMonthChange,
    billingDay,
    onBillingDayChange,
    selectedZone,
    onZoneChange,
    availableZones,
    ftRate,
    onFtRateChange,
    onRefresh,
    loading,
}) {
    const { t } = useTranslation();
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Generate year range (e.g. current year - 5 to current year + 2)
    const currentYear = new Date().getFullYear();
    const yearsList = [];
    for (let y = currentYear - 5; y <= currentYear + 2; y++) {
        yearsList.push(y);
    }
    if (!yearsList.includes(selectedYear)) {
        yearsList.push(selectedYear);
        yearsList.sort();
    }

    const handlePrevYear = () => {
        onYearChange(selectedYear - 1);
    };

    const handleNextYear = () => {
        onYearChange(selectedYear + 1);
    };

    return (
        <Paper
            elevation={2}
            sx={{
                p: { xs: 2, sm: 2.5 },
                mb: 3,
                borderRadius: 2,
                backdropFilter: 'blur(8px)',
                transition: 'all 0.2s ease-in-out',
            }}
        >
            {/* Main Controls Row */}
            <Grid container spacing={2} alignItems="center">
                {/* Year Selector with Stepper */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={t('common.previousYear', 'Previous Year')}>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={handlePrevYear}
                                    disabled={loading}
                                    sx={{ border: '1px solid', borderColor: 'divider' }}
                                >
                                    <ChevronLeft fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <FormControl fullWidth size="small">
                            <InputLabel id="power-cost-year-select-label">{t('common.year', 'Year')}</InputLabel>
                            <Select
                                labelId="power-cost-year-select-label"
                                id="power-cost-year-select"
                                value={selectedYear}
                                label={t('common.year', 'Year')}
                                onChange={(e) => onYearChange(parseInt(e.target.value, 10))}
                                disabled={loading}
                            >
                                {yearsList.map((yr) => (
                                    <MenuItem key={yr} value={yr}>
                                        {yr}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Tooltip title={t('common.nextYear', 'Next Year')}>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={handleNextYear}
                                    disabled={loading}
                                    sx={{ border: '1px solid', borderColor: 'divider' }}
                                >
                                    <ChevronRight fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </Grid>

                {/* Month Selector */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="power-cost-month-select-label">{t('common.month', 'Month')}</InputLabel>
                        <Select
                            labelId="power-cost-month-select-label"
                            id="power-cost-month-select"
                            value={selectedMonth}
                            label={t('common.month', 'Month')}
                            onChange={(e) => onMonthChange(e.target.value)}
                            disabled={loading}
                        >
                            <MenuItem value="all">
                                <em>{t('common.allMonths', 'All Months (12-Month Overview)')}</em>
                            </MenuItem>
                            {MONTH_KEYS.map((key, index) => {
                                const mNumber = index + 1;
                                return (
                                    <MenuItem key={key} value={mNumber}>
                                        {mNumber}. {t(`common.${key}`, getMonthName(mNumber, t))}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Billing Day Picker (1-31) */}
                <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="billing-day-select-label">{t('common.billingDay', 'Billing Day')}</InputLabel>
                        <Select
                            labelId="billing-day-select-label"
                            id="billing-day-select"
                            value={billingDay}
                            label={t('common.billingDay', 'Billing Day')}
                            onChange={(e) => onBillingDayChange(parseInt(e.target.value, 10))}
                            disabled={loading}
                        >
                            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                                <MenuItem key={d} value={d}>
                                    {d} ({d === 15 ? t('common.default', 'Default') : `${t('common.day', 'Day')} ${d}`})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Zone Filter */}
                <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="power-cost-zone-select-label">{t('common.zone', 'Zone')}</InputLabel>
                        <Select
                            labelId="power-cost-zone-select-label"
                            id="power-cost-zone-select"
                            value={selectedZone}
                            label={t('common.zone', 'Zone')}
                            onChange={(e) => onZoneChange(e.target.value)}
                            disabled={loading}
                        >
                            <MenuItem value="all">
                                <em>{t('common.wholeHouse', 'Whole House (All Zones)')}</em>
                            </MenuItem>
                            {availableZones.map((zoneName) => (
                                <MenuItem key={zoneName} value={zoneName}>
                                    {zoneName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Actions: Refresh and Advanced Toggle */}
                <Grid size={{ xs: 12, sm: 12, md: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1 }}>
                        <Tooltip title={t('common.refresh', 'Refresh Data')}>
                            <span>
                                <IconButton
                                    color="primary"
                                    onClick={onRefresh}
                                    disabled={loading}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'primary.main',
                                        '&:hover': { bgcolor: 'primary.light', color: 'white' },
                                    }}
                                >
                                    <Refresh
                                        sx={{
                                            animation: loading ? 'spin 1s linear infinite' : 'none',
                                            '@keyframes spin': {
                                                '0%': { transform: 'rotate(0deg)' },
                                                '100%': { transform: 'rotate(360deg)' },
                                            },
                                        }}
                                    />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip title={t('common.advancedSettings', 'Advanced Tariff Settings')}>
                            <IconButton
                                color={showAdvanced ? 'primary' : 'default'}
                                onClick={() => setShowAdvanced(!showAdvanced)}
                                sx={{ border: '1px solid', borderColor: 'divider' }}
                            >
                                <Tune fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Grid>
            </Grid>

            {/* Collapsible Advanced Settings (Ft Rate override) */}
            <Collapse in={showAdvanced}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tune fontSize="small" color="primary" />
                        {t('common.advancedSettings', 'Advanced Tariff Parameters')}
                    </Typography>

                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                                label={t('common.ftRate', 'Fuel Adjustment Charge (Ft)')}
                                type="number"
                                size="small"
                                fullWidth
                                value={ftRate}
                                onChange={(e) => onFtRateChange(e.target.value)}
                                inputProps={{ step: '0.0001', min: '0' }}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">฿/kWh</InputAdornment>,
                                }}
                                helperText={t('common.defaultFtRateNote', `Standard regulatory rate: ${DEFAULT_FT_RATE} ฿/kWh`)}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => onFtRateChange(DEFAULT_FT_RATE)}
                                sx={{ textTransform: 'none', height: 40 }}
                            >
                                {t('common.resetToDefault', 'Reset Ft to Default (0.1623 ฿)')}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Collapse>
        </Paper>
    );
}

export default PowerCostControlBar;
